import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileUp, Info, Upload } from 'lucide-react';
import { Button, Labeled, Modal } from '@tes/ui';
import { api } from '../lib/api';
import { isApiStatus, messageOf } from '../lib/errors';
import type {
  BaseFileRequiredError,
  FlaggedField,
  ImportTemplatesResult,
  TemplateExportDocument,
} from '../lib/types';

/** The API answers 409 with this when the referenced base PDF isn't on this server. */
function baseFileRequest(err: unknown): BaseFileRequiredError | null {
  if (!isApiStatus(err, 409)) return null;
  const d = err.detail as BaseFileRequiredError | undefined;
  return d?.baseFileRequired ? d : null;
}

/** Enough of a structural check to call the parsed JSON an export document. */
function isExportDocument(v: unknown): v is TemplateExportDocument {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { formatVersion?: unknown }).formatVersion === 'number'
  );
}

function FieldList({ fields }: { fields: FlaggedField[] }) {
  return (
    <ul className="mt-1 space-y-0.5">
      {fields.map((f) => (
        <li key={f.id} className="truncate">
          <span className="font-medium">{f.name}</span>
          <span className="opacity-70"> · page {f.page}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Import templates from an export file. The base PDF is only referenced, so the
 * import may come back asking for it — that's the normal path when the file came
 * from another server.
 *
 * The templates list is invalidated here, the moment an import lands — so
 * closing the dialog never has to guess whether anything changed.
 */
export function ImportTemplateDialog({
  onClose,
  onImported,
}: {
  onClose: () => void;
  onImported: (result: ImportTemplatesResult) => void;
}) {
  const qc = useQueryClient();
  const [doc, setDoc] = useState<TemplateExportDocument | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [needsPdf, setNeedsPdf] = useState<BaseFileRequiredError | null>(null);
  const [result, setResult] = useState<ImportTemplatesResult | null>(null);

  // The document travels as a mutation VARIABLE, not via the closure: the first
  // run fires in the same tick as setDoc(), so a closed-over `doc` would still
  // be the pre-update null and we'd POST a bare "null" body.
  const run = useMutation({
    mutationFn: ({ document, pdf }: { document: TemplateExportDocument; pdf?: File }) =>
      api.importTemplates(document, pdf),
    onSuccess: (r) => {
      setNeedsPdf(null);
      setResult(r);
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (e) => setNeedsPdf(baseFileRequest(e)),
  });

  const pickJson = async (file: File | null) => {
    setDoc(null);
    setParseError(null);
    setNeedsPdf(null);
    setResult(null);
    run.reset();
    if (!file) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch (e) {
      setParseError(`${file.name} isn't valid JSON — ${messageOf(e, 'could not be parsed')}`);
      return;
    }
    if (!isExportDocument(parsed)) {
      setParseError(`${file.name} doesn't contain a template export document`);
      return;
    }
    setDoc(parsed);
    run.mutate({ document: parsed });
  };

  const busy = run.isPending;
  // A 409 asking for the PDF is an expected step, not an error worth shouting about.
  const hardError = run.error && !needsPdf ? run.error : null;

  return (
    <Modal
      title="Import templates"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {result && (
            <Button onClick={() => onImported(result)}>
              {result.imported === 1 ? (
                <>
                  <FileUp size={16} className="mr-1" /> Open the draft
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-1" /> Done
                </>
              )}
            </Button>
          )}
        </>
      }
    >
        <div className="space-y-4">
          {!result && (
            <>
              <p className="text-sm text-slate-600">
                Imports always land as new <span className="font-medium">drafts</span> — nothing
                existing is modified. Variables already in your library are reused as-is.
              </p>
              <Labeled label="Template export file (.json)">
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => void pickJson(e.target.files?.[0] ?? null)}
                  className="w-full text-sm"
                />
              </Labeled>
            </>
          )}

          {parseError && <p className="text-sm text-red-600">{parseError}</p>}
          {busy && <p className="text-sm text-slate-400">Importing…</p>}
          {hardError && (
            <p className="text-sm text-red-600">{messageOf(hardError, 'Import failed')}</p>
          )}

          {needsPdf && (
            <div className="space-y-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
              <div className="flex gap-2">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <span className="font-medium">
                    The base PDF for “{needsPdf.name}” isn&apos;t on this server.
                  </span>{' '}
                  That&apos;s expected when the file came from another instance — attach the PDF to
                  finish.
                  <div className="mt-1 font-mono text-[11px] opacity-70">
                    expected {needsPdf.expected.pageSizes.length} page
                    {needsPdf.expected.pageSizes.length === 1 ? '' : 's'}
                    {needsPdf.expected.sha256 && ` · sha256 ${needsPdf.expected.sha256.slice(0, 12)}…`}
                  </div>
                </div>
              </div>
              <input
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  // `doc` is settled by now — this render is after the parse.
                  if (f && doc) run.mutate({ document: doc, pdf: f });
                }}
                className="w-full text-sm"
              />
            </div>
          )}

          {result && (
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2 rounded-lg bg-emerald-50 p-3 text-emerald-800">
                <Info size={16} className="mt-0.5 shrink-0" />
                <span>
                  Imported <span className="font-medium">{result.imported}</span> template
                  {result.imported === 1 ? '' : 's'} as draft
                  {result.imported === 1 ? '' : 's'}.
                </span>
              </div>

              {result.templates.map((t) => (
                <div key={t.templateId} className="rounded-lg border border-slate-200 p-3">
                  <div className="font-medium text-slate-900">{t.name}</div>
                  <ul className="mt-1 list-inside list-disc space-y-0.5 text-slate-600">
                    {t.variables.created.length > 0 && (
                      <li>
                        Added {t.variables.created.length} variable
                        {t.variables.created.length === 1 ? '' : 's'} to the library:{' '}
                        <span className="font-mono text-xs">{t.variables.created.join(', ')}</span>
                      </li>
                    )}
                    {t.variables.reused.length > 0 && (
                      <li>Reused {t.variables.reused.length} existing variable definition(s)</li>
                    )}
                  </ul>

                  {t.variables.differing.length > 0 && (
                    <div className="mt-2 rounded bg-slate-50 p-2 text-[12px] text-slate-600">
                      <span className="font-medium">Kept your existing definitions</span> for{' '}
                      <span className="font-mono">
                        {t.variables.differing.map((d) => d.key).join(', ')}
                      </span>{' '}
                      — the file described them differently. Nothing in your library was changed.
                    </div>
                  )}

                  {t.warnings.orphanedFields.length > 0 && (
                    <div className="mt-2 rounded bg-red-50 p-2 text-[12px] text-red-800">
                      <span className="font-medium">
                        {t.warnings.orphanedFields.length} field(s) dropped
                      </span>{' '}
                      — the PDF you attached has fewer pages than the design.
                      <FieldList fields={t.warnings.orphanedFields} />
                    </div>
                  )}
                  {t.warnings.outOfBoundsFields.length > 0 && (
                    <div className="mt-2 rounded bg-amber-50 p-2 text-[12px] text-amber-800">
                      <span className="font-medium">
                        {t.warnings.outOfBoundsFields.length} field(s) now hang off the page
                      </span>{' '}
                      — kept where they were, but move them before publishing.
                      <FieldList fields={t.warnings.outOfBoundsFields} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
    </Modal>
  );
}
